import { memo, ReactNode } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";

interface SmartLinkProps {
  to: string;
  children: ReactNode;
  className?: string;
  target?: string;
  rel?: string;
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * SmartLink conditionally renders either a React Router Link or a regular anchor tag
 * - When render=true query param is present (Puppeteer mode): Uses <a> tag for full page reload
 * - For regular users: Uses React Router <Link> for fast client-side navigation
 *
 * This ensures Puppeteer gets clean, full-page renders while users get SPA benefits
 */
const SmartLink = (props: SmartLinkProps) => {
  const { to, children, className, target, rel, onClick } = props;
  const [searchParams] = useSearchParams();

  // In render mode (Puppeteer), use regular anchor tags to force full page reload
  const isRenderMode = searchParams.get("render") === "true";

  if (isRenderMode) {
    return (
      <a
        href={to}
        className={className}
        target={target}
        rel={rel}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  // For regular users, use React Router Link for client-side navigation
  return (
    <RouterLink
      to={to}
      className={className}
      target={target}
      rel={rel}
      onClick={onClick}
    >
      {children}
    </RouterLink>
  );
};

export default memo(SmartLink);
